import { FC, Fragment, useEffect, useMemo } from 'react';
import { Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import { useApp } from '@/App';
import { useQuery } from '@tanstack/react-query';
import {
	getNAPAnalysis,
	getNAPAuthorityMember,
	getNAPBackgroundInfo,
	getNAPCompanyInformation,
	getNAPDetail,
	getNAPInternalComplianceDetail,
	getNAPRecommendationDetail,
	getNAPRiskMitigationDetail,
	getNAPWholesaleDetail,
	NAPCompanyInformation,
} from '@/libs/fleet-api/services/nap';
import dayjs from 'dayjs';
import { formatCurrency } from '@/libs/antd/formater';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import {
	getSlikRobotikResult,
	SlikRobotikDetail,
	SlikRobotikDetailArray,
} from '@/libs/fleet-api/services/prospect';

// import { getSlikRobotikDetail } from '@/libs/fleet-api/services/prospect';

const NAPDownloadPage: FC<{
	preview?: boolean;
	prospectId: string;
	redirect?: string;
	redirectProps?: unknown;
}> = ({ prospectId, redirect, redirectProps, preview = false }) => {
	const { navigate } = useApp();

	const getNAPDetailQuery = useQuery({
		queryKey: ['getNAPDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPDetail(id);
		},
	});

	const { napInformation, napNo } = getNAPDetailQuery?.data?.data ?? {};
	const { mainInformation } = napInformation ?? {};

	const getWholesaleDetailQuery = useQuery({
		queryKey: ['getNAPWholesaleDetail', prospectId] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const data = await getNAPWholesaleDetail(id);
			data.data.restruGpStatusOptions =
				data.data.restruGpStatusOptions.reverse();

			data.data.restruGpCovidStatusOptions =
				data.data.restruGpCovidStatusOptions.reverse();

			return data;
		},
	});

	const getNAPAuthorityMemberQuery = useQuery({
		queryKey: [
			'getNAPAuthorityMember',
			getWholesaleDetailQuery.data?.data?.authorityLevel?.id,
		] as const,
		queryFn: async ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			const res = await getNAPAuthorityMember({
				authorityLevelId: String(id),
				prospectId,
			});
			return res;
		},
		enabled: !!getWholesaleDetailQuery.data?.data?.authorityLevel?.id,
	});

	const getBackgroundInfoQuery = useQuery({
		queryKey: ['getNAPBackgroundInfo', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPBackgroundInfo(id);
		},
	});

	const getNAPCompanyInfoQuery = useQuery({
		queryKey: ['getNAPCompanyInformation', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPCompanyInformation(id);
		},
	});

	const getNAPInternalComplianceQuery = useQuery({
		queryKey: ['getNAPInternalComplianceDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPInternalComplianceDetail(id);
		},
	});

	// get result slik robotik
	const { data: slikRobotikResult } = useQuery({
		queryKey: ['getSlikRobotikResult', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getSlikRobotikResult(id);
		},
		enabled: !!prospectId,
	});

	// // get result detail robotik
	// const { data: slikRobotikDetail } =
	// 	useQuery({
	// 		queryKey: ['getSlikRobotikDetail', prospectId] as const,
	// 		queryFn: ({ queryKey }) => {
	// 			const [, id] = queryKey;
	// 			return getSlikRobotikDetail(id);
	// 		},
	// 		enabled: !!getNAPInternalComplianceQuery?.data?.data?.slikResult,
	// 	});

	const getNAPAnalysisQuery = useQuery({
		queryKey: ['getNAPAnalysis', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPAnalysis(prospectId);
		},
	});

	const getNAPRiskMitigationQuery = useQuery({
		queryKey: ['getNAPRiskMitigationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRiskMitigationDetail(id);
		},
	});

	const getNAPRecommendationQuery = useQuery({
		queryKey: ['getNAPRecommendationDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getNAPRecommendationDetail(id);
		},
	});

	const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.target as HTMLImageElement;
		img.style.display = 'none';
		console.error('Failed to load image:', img.src);
	};

	const loaded = useMemo(() => {
		return Boolean(
			getNAPDetailQuery.data &&
				getWholesaleDetailQuery.data &&
				getNAPAuthorityMemberQuery.data &&
				getBackgroundInfoQuery.data &&
				getNAPCompanyInfoQuery.data &&
				getNAPInternalComplianceQuery.data &&
				getNAPAnalysisQuery.data &&
				getNAPRiskMitigationQuery.data &&
				getNAPRecommendationQuery.data,
		);
	}, [
		getNAPDetailQuery.data,
		getWholesaleDetailQuery.data,
		getNAPAuthorityMemberQuery.data,
		getBackgroundInfoQuery.data,
		getNAPCompanyInfoQuery.data,
		getNAPInternalComplianceQuery.data,
		getNAPAnalysisQuery.data,
		getNAPRiskMitigationQuery.data,
		getNAPRecommendationQuery.data,
	]);

	useEffect(() => {
		// if (Date.now()) return;
		if (!loaded || preview) return;
		const printDoc = new jsPDF({
			orientation: 'portrait',
			unit: 'px',
			format: [1920, document.body.clientHeight],
			compress: true,
		});

		// Calculate center position
		const pageWidth = 1920;
		const contentWidth = document.body.clientWidth;
		const xOffset = (pageWidth - contentWidth) / 2;

		printDoc.html(document.body, {
			x: xOffset,
			y: 0,
			fontFaces: [
				{
					family: 'Balsamiq-Sans',
					weight: 'normal',
					stretch: 'normal',
					src: [
						{
							url: 'http://fonts.gstatic.com/s/balsamiqsans/v3/P5sEzZiAbNrN8SB3lQQX7Pnc8dkdIYdNHzs.ttf',
							format: 'truetype',
						},
					],
				},
			],
			image: {
				type: 'jpeg',
				quality: 0.5,
			},
			html2canvas: {
				logging: false,
				letterRendering: false,
				allowTaint: false,
				useCORS: true,
				width: 1920,
			},
			callback(doc) {
				doc.save(`NAP-NJF-${Date.now().toString()}`);
				if (redirect) {
					navigate(
						redirect as Parameters<typeof navigate>[0],
						redirectProps as Parameters<typeof navigate>[1],
					);
				} else {
					navigate('NAPPage', { prospectId });
				}
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loaded]);

	if (!loaded) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	const wholesaleFragment = () => {
		const { data } = getWholesaleDetailQuery.data ?? {};
		const allProposers = data?.proposers ?? [];
		const allCommittees = data?.recommendingCommittees ?? [];

		return (
			<table className="cell-border">
				<thead>
					<tr>
						<th
							className="bg"
							colSpan={4}
						>
							<center>
								NOTA ANALISA PEMBIAYAAN (NAP)
								<br />
								NON JF WHOLESALE
							</center>
						</th>
					</tr>
					<tr>
						<th
							style={{ background: '#e7e6e6' }}
							colSpan={4}
						>
							<center>
								NAP NON JF WHOLESALE No. {napNo} Tgl.{' '}
								{dayjs().utc(true).format('DD/MM/YYYY')}
							</center>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td style={{ width: 200 }}>
							<strong>Perihal</strong>
						</td>
						<td style={{ width: 20 }}>
							<center>:</center>
						</td>
						<td colSpan={2}>
							{data?.subjectOptions?.map((option, i) => (
								<Space
									key={i}
									style={{ display: 'flex' }}
								>
									{data.subject.id === option.id ? '[x]' : '[ ]'}
									<strong>{option.value}</strong>
								</Space>
							))}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Risalah Pipeline</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							{data?.pipelineSummaryDate &&
								dayjs(data.pipelineSummaryDate).format('DD/MM/YYYY')}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Nama Pemohon</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							<strong>{mainInformation?.customerName}</strong>
						</td>
					</tr>
					<tr>
						<td>
							<strong>Tipe Debitur</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							{data?.debtorTypeOptions.map((option) => (
								<Space
									key={option.id}
									style={{ display: 'flex' }}
								>
									{data.debtorType.id === option.id ? '[x]' : '[ ]'}
									<strong>{option.value}</strong>
								</Space>
							))}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Dasar Usulan</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							<strong>Reguler dan Captive MTF</strong>
							<ol>
								<li>Surat Permohonan Calon Debitur/Debitur tanggal ____</li>
							</ol>
							<strong>Captive BMRI</strong>
							<ol>
								<li>Surat Permohonan Calon Debitur/Debitur tanggal ____</li>
								<li>Form Referral Calon Debitur/Debitur tanggal ___</li>
							</ol>
						</td>
					</tr>
					<tr>
						<td>
							<strong>
								Informasi refferal dari BMRI apabila debitur Captive BMRI
							</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							<i>{data?.bmriReferralInformation ?? '(jika ada)'}</i>
						</td>
					</tr>
					<tr>
						<td rowSpan={allProposers?.length ? allProposers.length + 1 : 3}>
							<strong>Pengusul</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td className="bg">
							<center>
								<strong>Nama</strong>
							</center>
						</td>
						<td className="bg">
							<center>
								<strong>Jabatan</strong>
							</center>
						</td>
					</tr>
					{allProposers?.length
						? allProposers.map((record, i) => (
								<tr key={i}>
									<td>
										<center>:</center>
									</td>
									<td>
										{i + 1}. {record.name}
									</td>
									<td>
										{i + 1}. {record.role}
									</td>
								</tr>
							))
						: Array.from({ length: 2 }).map((_, i) => (
								<tr key={i}>
									<td>
										<center>:</center>
									</td>
									<td>{i + 1}. __</td>
									<td>{i + 1}. __</td>
								</tr>
							))}
					{allCommittees?.length ? (
						allCommittees.map((record, i) => (
							<tr key={i}>
								{i === 0 && (
									<td rowSpan={allCommittees.length}>
										<strong>Recommending Committee</strong>
									</td>
								)}
								<td>
									<center>:</center>
								</td>
								<td>
									{i + 1}. {record.name}
								</td>
								<td>
									{i + 1}. {record.role}
								</td>
							</tr>
						))
					) : (
						<Fragment>
							<tr>
								<td rowSpan={2}>
									<strong>Recommending Committee</strong>
								</td>
								<td>
									<center>:</center>
								</td>
								<td>1. __</td>
								<td>1. __</td>
							</tr>
							<tr>
								<td>
									<center>:</center>
								</td>
								<td>2. __</td>
								<td>2. __</td>
							</tr>
						</Fragment>
					)}
					<tr>
						<td>
							<strong>Kewenangan Memutus</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							{data?.authorityLevelOptions.map((record) => (
								<Space
									key={record.id}
									style={{ display: 'flex' }}
								>
									{data.authorityLevel.id === record.id ? '[x]' : '[ ]'}
									<strong>{record.value}</strong>
								</Space>
							))}
							<strong>
								Berdasarkan dengan nota/ketentuan {data?.regulationReference}
							</strong>
						</td>
					</tr>
					<tr>
						<td>
							<strong>
								Total Permohonan Fasilitas a.n. Calon Debitur dan Group Usaha
							</strong>
							<span>(jika ada)</span>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							<div style={{ textAlign: 'right' }}>(Rp. juta)</div>
							<table>
								<thead>
									<tr>
										<th className="bg">
											<center>Uraian</center>
										</th>
										<th className="bg">
											<center>Jenis Plafon</center>
										</th>
										<th className="bg">
											<center>Exp Date</center>
										</th>
										<th className="bg">
											<center>Limit Existing</center>
										</th>
										<th className="bg">
											<center>Limit Pengajuan</center>
										</th>
										<th className="bg">
											<center>Total Limit</center>
										</th>
									</tr>
								</thead>
								<tbody>
									{data?.totalFacilityRequest?.debtorPlafons?.length ? (
										data?.totalFacilityRequest?.debtorPlafons.map(
											(record, i) => (
												<tr key={i}>
													<td>
														{i === 0 && (
															<strong>Fasilitas a.n calon debitur</strong>
														)}
													</td>
													<td>{record.plafonType.value}</td>
													<td>
														{record.expiredDate &&
															dayjs(record.expiredDate).format('DD/MM/YYYY')}
													</td>
													<td>{formatCurrency(record.existingLimit)}</td>
													<td>{formatCurrency(record.applicationLimit)}</td>
													<td>{formatCurrency(record.totalLimit)}</td>
												</tr>
											),
										)
									) : (
										<tr>
											<td>
												<strong>Fasilitas a.n calon debitur</strong>
											</td>
											<td></td>
											<td></td>
											<td></td>
											<td></td>
											<td></td>
										</tr>
									)}
									<tr>
										<td className="bg">
											<strong>Total Debitur</strong>
										</td>
										<td className="bg"></td>
										<td className="bg"></td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.debtorTotal
													?.existingLimit ?? 0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.debtorTotal
													?.applicationLimit ?? 0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.debtorTotal?.totalLimit ??
													0,
											)}
										</td>
									</tr>
									{data?.totalFacilityRequest?.businessGroups?.length ? (
										data.totalFacilityRequest.businessGroups.map(
											(record, i) => (
												<tr key={i}>
													<td>{record.name}</td>
													<td>{record.plafonType.value}</td>
													<td>
														{record.expiredDate &&
															dayjs(record.expiredDate).format('DD/MM/YYYY')}
													</td>
													<td>{formatCurrency(record.existingLimit)}</td>
													<td>{formatCurrency(record.applicationLimit)}</td>
													<td>{formatCurrency(record.totalLimit)}</td>
												</tr>
											),
										)
									) : (
										<tr>
											<td>&nbsp;</td>
											<td></td>
											<td></td>
											<td></td>
											<td></td>
											<td></td>
										</tr>
									)}
									<tr>
										<td className="bg">
											<strong>Total Group Usaha</strong>
										</td>
										<td className="bg"></td>
										<td className="bg"></td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.businessGroupTotal
													?.existingLimit ?? 0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.businessGroupTotal
													?.applicationLimit ?? 0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.businessGroupTotal
													?.totalLimit ?? 0,
											)}
										</td>
									</tr>
									<tr>
										<td colSpan={6} />
									</tr>
									<tr>
										<td style={{ background: '#e7e6e6' }}>
											<strong>Total Exposure JF</strong>
										</td>
										<td style={{ background: '#e7e6e6' }}></td>
										<td style={{ background: '#e7e6e6' }}></td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.jfTotalExposure
													?.existingLimit ?? 0,
											)}
										</td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.jfTotalExposure
													?.applicationLimit ?? 0,
											)}
										</td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.jfTotalExposure
													?.totalLimit ?? 0,
											)}
										</td>
									</tr>
									<tr>
										<td style={{ background: '#e7e6e6' }}>
											<strong>Total Exposure NJF</strong>
										</td>
										<td style={{ background: '#e7e6e6' }}></td>
										<td style={{ background: '#e7e6e6' }}></td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.njfTotalExposure
													?.existingLimit ?? 0,
											)}
										</td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.njfTotalExposure
													?.applicationLimit ?? 0,
											)}
										</td>
										<td style={{ background: '#e7e6e6' }}>
											{formatCurrency(
												data?.totalFacilityRequest?.njfTotalExposure
													?.totalLimit ?? 0,
											)}
										</td>
									</tr>
									<tr>
										<td className="bg">
											<strong>Grand Total</strong>
										</td>
										<td className="bg"></td>
										<td className="bg"></td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.grandTotal?.existingLimit ??
													0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.grandTotal
													?.applicationLimit ?? 0,
											)}
										</td>
										<td className="bg">
											{formatCurrency(
												data?.totalFacilityRequest?.grandTotal?.totalLimit ?? 0,
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td>
							<strong>Aging Date</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td colSpan={2}>
							{data?.agingDate && dayjs(data.agingDate).format('DD/MM/YYYY')}
						</td>
					</tr>
					<tr>
						<td>
							<strong>Informasi Tambahan</strong>
						</td>
						<td>
							<center>:</center>
						</td>
						<td
							colSpan={2}
							style={{ height: 100 }}
						>
							{data?.notes}
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const applicationBGFragment = () => {
		const { data } = getBackgroundInfoQuery?.data ?? {};

		return (
			<table>
				<thead className="cell-border">
					<tr>
						<td
							className="bg"
							colSpan={2}
						>
							<strong>A. LATAR BELAKANG PERMOHONAN</strong>
						</td>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Kebutuhan Pembiayaan dan Penggunaan Unit</strong>
							<table className="cell-border cell-small">
								<thead>
									<tr>
										<th className="bg">Jumlah Unit</th>
										<th className="bg">Detail Merk Unit</th>
										<th className="bg">Harga</th>
										<th className="bg">Lokasi Project</th>
									</tr>
								</thead>
								<tbody>
									{data?.financingAndUnitRequirements?.length
										? data?.financingAndUnitRequirements?.map((record, i) => (
												<tr key={i}>
													<td>{record.numberOfUnits}</td>
													<td>{record.unitBrandDetails}</td>
													<td>{formatCurrency(record?.price ?? 0)}</td>
													<td>{record.projectLocation}</td>
												</tr>
											))
										: Array.from({ length: 2 }).map((_, i) => (
												<tr key={i}>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td colSpan={2} />
					</tr>
					<tr>
						<td>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi History Approval Kondisi Khusus</strong>
							<i>(jika ada)</i>
							<table className="cell-border cell-small">
								<thead>
									<tr>
										<th className="bg">Perihal</th>
										<th className="bg">Ketentuan</th>
										<th className="bg">Tanggal Disetujui</th>
										<th className="bg">Pemutus Terakhir</th>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 2 }).map((_, i) => (
										<tr key={i}>
											<td>&nbsp;</td>
											<td>&nbsp;</td>
											<td>&nbsp;</td>
											<td>&nbsp;</td>
										</tr>
									))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td colSpan={2} />
					</tr>
					<tr>
						<td>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi Pengajuan Kondisi Khusus</strong>
							<i>(jika ada)</i>
							<table className="cell-border cell-small">
								<thead>
									<tr>
										<th className="bg">Perihal</th>
										<th className="bg">Ketentuan</th>
										<th className="bg">Usulan</th>
										<th className="bg">Pemutus Terakhir</th>
									</tr>
								</thead>
								<tbody>
									{data?.specialConditionSubmission?.length
										? data.specialConditionSubmission.map((record, i) => (
												<tr key={i}>
													<td>{record.subject}</td>
													<td>{record.provisions}</td>
													<td>
														{'proposal' in record &&
															(record.proposal as string)}
													</td>
													<td>
														<ul>
															{(
																record.finalApprover as unknown as number[]
															).map((record, i) => (
																<li key={i}>
																	{
																		data.finalApproverOptions.find(
																			(f) => f.id === record,
																		)?.value
																	}
																</li>
															))}
														</ul>
													</td>
												</tr>
											))
										: Array.from({ length: 2 }).map((_, i) => (
												<tr key={i}>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
													<td>&nbsp;</td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td
							colSpan={2}
							style={{ height: 100 }}
						>
							Informasi Tambahan :
							<br />
							{data?.notes}
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const companyGroupFragment = () => {
		const { data } = getNAPCompanyInfoQuery?.data ?? {};

		return (
			<table>
				<thead className="cell-border">
					<tr>
						<td
							className="bg"
							colSpan={2}
						>
							<strong>B. INFORMASI PERUSAHAAN DAN GROUP USAHA</strong>
						</td>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi Umum Perusahaan</strong>
							<table className="cell-border">
								<tbody>
									<tr>
										<td style={{ width: 160 }}>Nama Debitur</td>
										<td>{mainInformation?.customerName}</td>
									</tr>
									<tr>
										<td>Alamat Kantor/Usaha</td>
										<td>
											{[
												data?.address,
												data?.rt && data?.rw && `${data.rt}/${data.rw}`,
												data?.postal?.postalCode,
												data?.postal?.locationName,
												data?.postal?.districtName,
												data?.postal?.cityName,
											]
												.filter((d) => d)
												.join(', ')}
										</td>
									</tr>
									<tr>
										<td>Bidang Usaha</td>
										<td>{data?.industrySector}</td>
									</tr>
									<tr>
										<td>Key Person</td>
										<td>{data?.keyPerson}</td>
									</tr>
									<tr>
										<td>Jabatan Key Person</td>
										<td>{data?.position.value}</td>
									</tr>
									<tr>
										<td>Tanggal Berdiri Perusahaan</td>
										<td>
											{data?.companyEstablishmentDate &&
												dayjs(data.companyEstablishmentDate).format(
													'DD/MM/YYYY',
												)}
										</td>
									</tr>
									<tr>
										<td>Tahun berhubungan dengan MTF/BMRI</td>
										<td>{data?.relationshipYear}</td>
									</tr>
									<tr>
										<td>Mulai Usaha Terhadap Sektor Yang Dibiayai</td>
										<td>
											{data?.financedSectorBusinessStart &&
												dayjs(data.financedSectorBusinessStart).format(
													'DD/MM/YYYY',
												)}
										</td>
									</tr>
									<tr>
										<td>Jumlah Karyawan</td>
										<td>{data?.numberOfEmployees}</td>
									</tr>
									<tr>
										<td>BMPP</td>
										<td>
											{data?.bmppOptions.map((record, i) => (
												<Space
													key={i}
													style={{ display: 'flex' }}
												>
													{data.bmpp.id === record.id ? '[x]' : '[ ]'}
													<span>{record.value}</span>
												</Space>
											))}
										</td>
									</tr>
									<tr>
										<td>Susunan Pengurus</td>
										<td>
											berdasarkan akta No.{data?.latestManagementDeedNumber}{' '}
											Tahun {data?.latestManagementDeedYear}
											<table className="cell-border cell-small">
												<thead>
													<tr>
														<th
															className="bg"
															style={{ width: 30 }}
														>
															No
														</th>
														<th className="bg">Nama</th>
														<th className="bg">Jabatan</th>
														<th className="bg">Status</th>
													</tr>
												</thead>
												<tbody>
													{data?.companyManagements?.length
														? data.companyManagements.map((record, i) => (
																<tr key={i}>
																	<td>{i + 1}</td>
																	<td>{record.name}</td>
																	<td>
																		{'positionId' in record &&
																			(record.positionId as MasterData)?.value}
																	</td>
																	<td>Aktif</td>
																</tr>
															))
														: Array.from({ length: 4 }).map((_, i) => (
																<tr key={i}>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																</tr>
															))}
												</tbody>
											</table>
										</td>
									</tr>
									<tr>
										<td>Pemegang Saham</td>
										<td>
											berdasarkan akta No.{data?.latestShareholderDeedNumber}{' '}
											Tahun {data?.latestShareholderDeedYear}
											<table className="cell-border cell-small">
												<thead>
													<tr>
														<th
															className="bg"
															style={{ width: 30 }}
														>
															No
														</th>
														<th className="bg">Nama</th>
														<th className="bg">Jabatan</th>
														<th className="bg">Saham (lembar)</th>
														<th className="bg">Nilai (Rp. Juta)</th>
														<th className="bg">Porsi (%)</th>
													</tr>
												</thead>
												<tbody>
													{data?.companyShareholders?.length
														? data.companyShareholders.map((record, i) => (
																<tr key={i}>
																	<td>{i + 1}</td>
																	<td>{record.name}</td>
																	<td></td>
																	<td></td>
																	<td></td>
																	<td>{record.shareholding}</td>
																</tr>
															))
														: Array.from({ length: 4 }).map((_, i) => (
																<tr key={i}>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																	<td>&nbsp;</td>
																</tr>
															))}
													<tr>
														<td colSpan={3}>
															<center>
																<strong>Total</strong>
															</center>
														</td>
														<td>&nbsp;</td>
														<td>&nbsp;</td>
														<td>
															{data?.companyShareholders?.reduce(
																(acc, curr) => acc + curr.shareholding,
																0,
															)}
														</td>
													</tr>
												</tbody>
											</table>
										</td>
									</tr>
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi Umum Group</strong>
							{(
								data?.companyGroups ??
								([{}] as NAPCompanyInformation['companyGroups'])
							).map((record, i) => {
								const {
									companyGroupGeneralInformation,
									managementStructures,
									shareholderStructures,
								} = record ?? {};

								return (
									<Fragment key={i}>
										{(data?.companyGroups?.length ?? 1) > 1 && (
											<h3>Group Usaha {i + 1}</h3>
										)}
										<table className="cell-border">
											<tbody>
												<tr>
													<td style={{ width: 160 }}>Nama Entitas</td>
													<td>{companyGroupGeneralInformation?.entityName}</td>
												</tr>
												<tr>
													<td>Bidang Usaha</td>
													<td>
														{
															companyGroupGeneralInformation?.industrySector
																?.value
														}
													</td>
												</tr>
												<tr>
													<td>Tahun berhubungan dengan MTF/BMRI</td>
													<td>
														{companyGroupGeneralInformation?.relationshipYear}
													</td>
												</tr>
												<tr>
													<td>Key Person</td>
													<td>{companyGroupGeneralInformation?.keyPerson}</td>
												</tr>
												<tr>
													<td>Susunan Pengurus</td>
													<td>
														berdasarkan akta No. Tahun
														<table className="cell-border cell-small">
															<thead>
																<tr>
																	<th
																		className="bg"
																		style={{ width: 30 }}
																	>
																		No
																	</th>
																	<th className="bg">Nama</th>
																	<th className="bg">Jabatan</th>
																	<th className="bg">Status</th>
																</tr>
															</thead>
															<tbody>
																{managementStructures?.length
																	? managementStructures.map((d, i) => (
																			<tr key={i}>
																				<td>{i + 1}</td>
																				<td>{d.name}</td>
																				<td>{d.positionId.value}</td>
																				<td>Aktif</td>
																			</tr>
																		))
																	: Array.from({ length: 4 }).map((_, i) => (
																			<tr key={i}>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																			</tr>
																		))}
															</tbody>
														</table>
													</td>
												</tr>
												<tr>
													<td>Pemegang Saham</td>
													<td>
														berdasarkan akta No. Tahun
														<table className="cell-border cell-small">
															<thead>
																<tr>
																	<th
																		className="bg"
																		style={{ width: 30 }}
																	>
																		No
																	</th>
																	<th className="bg">Nama</th>
																	<th className="bg">Jabatan</th>
																	<th className="bg">Saham (lembar)</th>
																	<th className="bg">Nilai (Rp. Juta)</th>
																	<th className="bg">Porsi (%)</th>
																</tr>
															</thead>
															<tbody>
																{shareholderStructures?.length
																	? shareholderStructures.map((d, i) => (
																			<tr key={i}>
																				<td>{i + 1}</td>
																				<td>{d.name}</td>
																				<td>{d.positionId.value}</td>
																				<td>{d.shareAmount}</td>
																				<td>
																					{formatCurrency(d.shareValue ?? 0)}
																				</td>
																				<td>{d.shareholding ?? 0}%</td>
																			</tr>
																		))
																	: Array.from({ length: 4 }).map((_, i) => (
																			<tr key={i}>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																				<td>&nbsp;</td>
																			</tr>
																		))}
																<tr>
																	<td colSpan={3}>
																		<center>
																			<strong>Total</strong>
																		</center>
																	</td>
																	<td>
																		{shareholderStructures?.reduce(
																			(acc, curr) => acc + curr.shareAmount,
																			0,
																		)}
																	</td>
																	<td>
																		{formatCurrency(
																			shareholderStructures?.reduce(
																				(acc, curr) => acc + curr.shareValue,
																				0,
																			),
																		)}
																	</td>
																	<td>
																		{shareholderStructures?.reduce(
																			(acc, curr) => acc + curr.shareholding,
																			0,
																		)}
																		%
																	</td>
																</tr>
															</tbody>
														</table>
													</td>
												</tr>
											</tbody>
										</table>
									</Fragment>
								);
							})}
						</td>
					</tr>
					<tr>
						<td>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>Company Tree</strong>
							<ul style={{ paddingLeft: 16 }}>
								<li>
									Ultimate Beneficial Owner : {data?.ultimateBeneficialOwner}
								</li>
								<li>
									One Obligor/Konsolidasi Exposure :
									<br />
									{data?.exposureDocument?.url && (
										<img
											src={data.exposureDocument.url}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
								</li>
								<li>
									Company Tree :
									<br />
									{data?.companyTreeDocument?.url && (
										<img
											src={data.companyTreeDocument.url}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
								</li>
							</ul>
						</td>
					</tr>
					<tr>
						<td>
							<center>
								<strong>IV.</strong>
							</center>
						</td>
						<td>
							<strong>
								Latar Belakang dan Sejarah Perusahaan (bisnis unit)
							</strong>
							<ul style={{ paddingLeft: 16 }}>
								<li>Latar belakang Perusahaan : {data?.companyBackground}</li>
								<li>
									Perkembangan Bisnis dari tahun ke tahun :{' '}
									{data?.yearlyBusinessDevelopment}
								</li>
							</ul>
						</td>
					</tr>
					<tr>
						<td
							colSpan={2}
							style={{ height: 100 }}
						>
							Informasi Tambahan :
							<br />
							{data?.notes}
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const fullfillmentFragment = () => {
		const { data } = getNAPInternalComplianceQuery.data ?? {};
		const { napInformation } = getNAPDetailQuery?.data?.data ?? {};
		const isRobotik = data?.slikResult ?? false;
		// const manualSlikData = data?.manualSlikResult ?? null;

		const robotikCustomerRef = slikRobotikResult?.data?.details?.filter(
			(detail) => detail.role === 'Customers',
		);
		const customerSelectedIndexes = robotikCustomerRef?.[0]?.selectedIndexes
			? robotikCustomerRef[0].selectedIndexes.split(',').map(Number)
			: [];

		const robotikDetail: SlikRobotikDetailArray =
			isRobotik && Array.isArray(data?.slikResult) && data.slikResult.length > 0
				? data.slikResult
				: [];

		// managementshareholdertype:
		// 0: pemegang saham
		// 1: pengurus
		// 2: pengurus & pemegang saham

		const getManagementShareholderType = (detail: SlikRobotikDetail) => {
			const notCompanyRefs = slikRobotikResult?.data?.details.filter(
				(d) => d.role !== 'Customers',
			);
			const getResultItem = notCompanyRefs?.find(
				(d) =>
					d.referenceNumber ===
					(detail as unknown as SlikRobotikDetail[0])?.ReferenceNumber,
			);

			switch (getResultItem?.role) {
				case 'CustomerCompanyManagementShareholderInformation':
					return 2;
				case 'CustomerCompanyManagementInformation':
					return 1;
				case 'CustomerCompanyShareholderInformation':
					return 0;
				default:
					return '-';
			}
		};

		const getManagementShareholderLabel = (detail: SlikRobotikDetail) => {
			switch (getManagementShareholderType(detail)) {
				case 2:
					return 'Pengurus & Pemegang Saham';
				case 1:
					return 'Pengurus';
				case 0:
					return 'Pemegang Saham';
				default:
					return '-';
			}
		};

		const slikManualResult = data?.manualSlikResult;

		return (
			<div style={{ width: '100%', maxWidth: '1600px' }}>
				<table style={{ width: '100%', maxWidth: '1600px' }}>
					<thead className="cell-border">
						<tr>
							<td
								className="bg"
								colSpan={2}
							>
								<strong>C. PEMENUHAN KETENTUAN INTERNAL MTF</strong>
							</td>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>
								<center>
									<strong>I.</strong>
								</center>
							</td>
							<td>
								<strong>Portofolio Guideline :</strong>
								<div style={{ minHeight: 60 }}>
									{data?.portofolioGuideline && (
										<div
											dangerouslySetInnerHTML={{
												__html: data.portofolioGuideline,
											}}
										/>
									)}
								</div>
								<div></div>
								<div style={{ minHeight: 60 }}>
									Informasi Tambahan Portofolio Guideline:
									<br />
									{data?.additionalPortofolioGuideline && (
										<div
											dangerouslySetInnerHTML={{
												__html: data.additionalPortofolioGuideline,
											}}
										/>
									)}
								</div>
							</td>
						</tr>
						<tr>
							<td>
								<center>
									<strong>II.</strong>
								</center>
							</td>
							<td>
								<strong>Rating Customer</strong>
								<div style={{ minHeight: 60 }}>
									{data?.customerRating?.value}
								</div>
								Evidence Rating Customer :
								<div style={{ minHeight: 60 }}>
									{data?.customerRatingEvidenceDocument?.url && (
										<img
											src={data.customerRatingEvidenceDocument.url}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
								</div>
								Informasi Rating Customer :
								<div style={{ minHeight: 60 }}>
									{data?.customerRatingInformation}
								</div>
							</td>
						</tr>
						<tr>
							<td>
								<center>
									<strong>III.</strong>
								</center>
							</td>
							<td>
								<strong>Rating Bouwheer</strong>
								<div style={{ minHeight: 60 }}>
									{data?.bouwheerRating?.value}
								</div>
								Evidence Rating Bouwheer :
								<div style={{ minHeight: 60 }}>
									{data?.bouwheerRatingEvidenceDocument?.url && (
										<img
											src={data.bouwheerRatingEvidenceDocument.url}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
								</div>
								Informasi Rating Bouwheer :
								<div style={{ minHeight: 60 }}>
									{data?.bouwheerRatingInformation}
								</div>
							</td>
						</tr>
						<tr>
							<td>
								<center>
									<strong>IV.</strong>
								</center>
							</td>
							<td>
								<div style={{ width: '100%', maxWidth: '100%' }}>
									<strong>SLIK OJK dan Pengurus</strong>
									{data?.slikResult && robotikDetail.length > 0 ? (
										<Fragment>
											<p style={{ color: 'red' }}>
												Prospect ini menggunakan SLIK Robotik
											</p>
											<p>
												<strong>
													Nama Debitur:{' '}
													{(robotikDetail[0] as unknown as SlikRobotikDetail[0])
														?.RPT_SUMMARY_COMPANY?.[0]?.namaDebitur ?? '-'}
												</strong>
											</p>
											<table
												className="cell-border cell-small"
												style={{
													marginBottom: 20,
													whiteSpace: 'pre-wrap',
													wordBreak: 'break-word',
													width: '100%',
													fontSize: 10,
												}}
												width="100%"
											>
												<thead>
													<tr>
														<th rowSpan={2}>No</th>
														<th rowSpan={2}>Pelapor</th>
														<th rowSpan={2}>
															Tanggal Update <br />
															Terakhir
														</th>
														<th colSpan={3}>Jenis Penggunaan</th>
														<th rowSpan={2}>Limit</th>
														<th rowSpan={2}>Baki Debet</th>
														<th colSpan={3}>Bunga (%)</th>
														<th colSpan={2}>Tunggakan</th>
														<th rowSpan={2}>Tanggal Jatuh Tempo</th>
														<th rowSpan={2}>Tahun Bulan Kol Akhir</th>
														<th rowSpan={2}>Max Kol</th>
													</tr>
													<tr>
														<th>Investasi</th>
														<th>Modal Kerja</th>
														<th>Konsumsi</th>
														<th>Terendah</th>
														<th>Maksimal</th>
														<th>Rata-Rata</th>
														<th>Pokok</th>
														<th>Bunga</th>
													</tr>
												</thead>
												<tbody>
													{robotikDetail.length > 0
														? (
																(
																	robotikDetail[0] as unknown as SlikRobotikDetail[0]
																).VW_RPT_SUMMARY_PEL ?? []
															)
																.filter(
																	(
																		record: SlikRobotikDetail[0]['VW_RPT_SUMMARY_PEL'][number],
																	) => {
																		const selectedCompanies = (
																			robotikDetail[0] as unknown as SlikRobotikDetail[0]
																		).RPT_SUMMARY_COMPANY?.filter(
																			(
																				_: SlikRobotikDetail[0]['RPT_SUMMARY_COMPANY'][number],
																				i: number,
																			) => customerSelectedIndexes.includes(i),
																		);
																		return selectedCompanies?.some(
																			(
																				company: SlikRobotikDetail[0]['RPT_SUMMARY_COMPANY'][number],
																			) => company.npwp === record.identitasnew,
																		);
																	},
																)
																?.map(
																	(
																		detail: SlikRobotikDetail[0]['VW_RPT_SUMMARY_PEL'][number],
																		index: number,
																	) => (
																		<tr key={index}>
																			<td align="center">{index + 1}</td>
																			<td align="left">{detail.ljkKet}</td>
																			<td align="center">
																				{detail.tanggalUpdate
																					? dayjs(detail.tanggalUpdate).format(
																							'DD/MM/YYYY',
																						)
																					: '-'}
																			</td>
																			<td align="center">
																				{detail.jenisPenggunaan2}
																			</td>
																			<td align="center">
																				{detail.jenisPenggunaan1}
																			</td>
																			<td align="center">
																				{detail.jenisPenggunaan3}
																			</td>
																			<td align="right">
																				{detail.plafon
																					? formatCurrency(
																							Number(detail.plafon),
																						)
																					: '-'}
																			</td>
																			<td align="right">
																				{detail.bakiDebet
																					? formatCurrency(
																							Number(detail.bakiDebet),
																						)
																					: '-'}
																			</td>
																			<td align="center">
																				{detail.sukuBungamin}
																			</td>
																			<td align="center">
																				{detail.sukuBungamax}
																			</td>
																			<td align="center">
																				{detail.sukuBungaavg}
																			</td>
																			<td align="right">
																				{detail.tunggakanPokok
																					? formatCurrency(
																							Number(detail.tunggakanPokok),
																						)
																					: 0}
																			</td>
																			<td align="right">
																				{detail.tunggakanBunga
																					? formatCurrency(
																							Number(detail.tunggakanBunga),
																						)
																					: 0}
																			</td>
																			<td align="center">
																				{detail.tanggalJatuhTempo
																					? dayjs(
																							detail.tanggalJatuhTempo,
																						).format('DD/MM/YYYY')
																					: '-'}
																			</td>
																			<td align="center">
																				{detail.tahunBulanKolAkhir}
																			</td>
																			<td align="center">{detail.maxkol}</td>
																		</tr>
																	),
																)
														: null}
												</tbody>
											</table>

											{/* table for each company management */}

											{robotikDetail.length > 1
												? robotikDetail
														.slice(1, robotikDetail.length)
														?.map(
															(detail: SlikRobotikDetail, index: number) => (
																<div key={index}>
																	<p>
																		<strong>
																			Nama{' '}
																			{getManagementShareholderLabel(detail)}:{' '}
																			{(
																				detail as unknown as SlikRobotikDetail[0]
																			)?.RPT_SUMMARY_PERSONAL?.[0]
																				?.namaDebitur ?? '-'}
																		</strong>
																	</p>
																	<table
																		className="cell-border cell-small"
																		style={{
																			marginBottom: 20,
																			whiteSpace: 'pre-wrap',
																			wordBreak: 'break-word',
																			width: '100%',
																			fontSize: 10,
																		}}
																		width="100%"
																	>
																		<thead>
																			<tr>
																				<th rowSpan={2}>No</th>
																				<th
																					rowSpan={2}
																					style={{
																						minWidth: '300px',
																						maxWidth: '300px',
																						whiteSpace: 'wrap',
																					}}
																				>
																					Pelapor
																				</th>
																				<th rowSpan={2}>
																					Tanggal Update <br />
																					Terakhir
																				</th>
																				<th colSpan={3}>Jenis Penggunaan</th>
																				<th rowSpan={2}>Limit</th>
																				<th rowSpan={2}>Baki Debet</th>
																				<th colSpan={3}>Bunga (%)</th>
																				<th colSpan={2}>Tunggakan</th>
																				<th rowSpan={2}>Tanggal Jatuh Tempo</th>
																				<th rowSpan={2}>
																					Tahun Bulan Kol Akhir
																				</th>
																				<th rowSpan={2}>Max Kol</th>
																			</tr>
																			<tr>
																				<th>Investasi</th>
																				<th>Modal Kerja</th>
																				<th>Konsumsi</th>
																				<th>Terendah</th>
																				<th>Maksimal</th>
																				<th>Rata-Rata</th>
																				<th>Pokok</th>
																				<th>Bunga</th>
																			</tr>
																		</thead>
																		<tbody>
																			{detail
																				? (
																						(
																							detail as unknown as SlikRobotikDetail[0]
																						).VW_RPT_SUMMARY_PEL ?? []
																					)
																						.filter(
																							(
																								record: SlikRobotikDetail[0]['VW_RPT_SUMMARY_PEL'][number],
																							) => {
																								// if company
																								const isCompany =
																									(
																										detail as unknown as SlikRobotikDetail[0]
																									)?.RPT_SUMMARY_COMPANY
																										?.length > 0;
																								// console.log(isCompany, detail);
																								if (isCompany) {
																									const selectedCompanies = (
																										detail as unknown as SlikRobotikDetail[0]
																									).RPT_SUMMARY_COMPANY?.filter(
																										(
																											_: SlikRobotikDetail[0]['RPT_SUMMARY_COMPANY'][number],
																											i: number,
																										) =>
																											customerSelectedIndexes.includes(
																												i,
																											),
																									);
																									return selectedCompanies?.some(
																										(
																											company: SlikRobotikDetail[0]['RPT_SUMMARY_COMPANY'][number],
																										) =>
																											company.npwp ===
																											record.identitasnew,
																									);
																								} else {
																									return true;
																								}
																							},
																						)
																						?.map(
																							(
																								detail: SlikRobotikDetail[0]['VW_RPT_SUMMARY_PEL'][number],
																								index: number,
																							) => (
																								<tr key={index}>
																									<td align="center">
																										{index + 1}
																									</td>
																									<td
																										align="left"
																										style={{
																											minWidth: '300px',
																											maxWidth: '300px',
																											whiteSpace: 'wrap',
																										}}
																									>
																										{detail.ljkKet}
																									</td>
																									<td align="center">
																										{detail.tanggalUpdate
																											? dayjs(
																													detail.tanggalUpdate,
																												).format('DD/MM/YYYY')
																											: '-'}
																									</td>
																									<td align="center">
																										{detail.jenisPenggunaan2}
																									</td>
																									<td align="center">
																										{detail.jenisPenggunaan1}
																									</td>
																									<td align="center">
																										{detail.jenisPenggunaan3}
																									</td>
																									<td align="right">
																										{detail.plafon
																											? formatCurrency(
																													Number(detail.plafon),
																												)
																											: '-'}
																									</td>
																									<td align="right">
																										{detail.bakiDebet
																											? formatCurrency(
																													Number(
																														detail.bakiDebet,
																													),
																												)
																											: '-'}
																									</td>
																									<td align="center">
																										{detail.sukuBungamin}
																									</td>
																									<td align="center">
																										{detail.sukuBungamax}
																									</td>
																									<td align="center">
																										{detail.sukuBungaavg}
																									</td>
																									<td align="right">
																										{detail.tunggakanPokok
																											? formatCurrency(
																													Number(
																														detail.tunggakanPokok,
																													),
																												)
																											: 0}
																									</td>
																									<td align="right">
																										{detail.tunggakanBunga
																											? formatCurrency(
																													Number(
																														detail.tunggakanBunga,
																													),
																												)
																											: 0}
																									</td>
																									<td align="center">
																										{detail.tanggalJatuhTempo
																											? dayjs(
																													detail.tanggalJatuhTempo,
																												).format('DD/MM/YYYY')
																											: '-'}
																									</td>
																									<td align="center">
																										{detail.tahunBulanKolAkhir}
																									</td>
																									<td align="center">
																										{detail.maxkol}
																									</td>
																								</tr>
																							),
																						)
																				: null}
																		</tbody>
																	</table>
																</div>
															),
														)
												: null}
										</Fragment>
									) : (
										<Fragment>
											<p style={{ color: 'red' }}>
												Prospect ini menggunakan SLIK Manual
											</p>
											<table
												className="cell-border cell-small"
												style={{ marginBottom: 20, fontSize: 10 }}
											>
												<thead>
													<tr>
														<th className="bg">No</th>
														<th className="bg">Nama Terlapor</th>
														<th className="bg">Jabatan</th>
														<th className="bg">Hasil SLIK</th>
													</tr>
												</thead>
												<tbody>
													<tr>
														<td>1</td>
														<td>
															{napInformation?.mainInformation?.customerName ??
																'-'}
														</td>
														<td>Debitur</td>
														<td>
															{napInformation?.mainInformation?.slikResult ??
																'-'}
														</td>
													</tr>
													{slikManualResult?.managementSlikInformation?.map(
														(item, index) => (
															<tr key={index}>
																<td>{index + 2}</td>
																<td>{item.name}</td>
																<td>Pengurus</td>
																<td>{item.slikResult ?? '-'}</td>
															</tr>
														),
													)}
													{slikManualResult?.shareholderSlikInformation?.map(
														(item, index) => (
															<tr key={index}>
																<td>
																	{index +
																		2 +
																		slikManualResult?.managementSlikInformation
																			?.length}
																</td>
																<td>{item.name}</td>
																<td>Pemegang Saham</td>
																<td>{item.slikResult ?? '-'}</td>
															</tr>
														),
													)}
												</tbody>
											</table>
										</Fragment>
									)}
								</div>
							</td>
						</tr>
						<tr>
							<td />
							<td style={{ height: 100 }}>
								<strong>Catatan :</strong>
								<p>{data?.notes ?? '-'}</p>
							</td>
						</tr>
						<tr>
							<td>
								<center>
									<strong>V.</strong>
								</center>
							</td>
							<td>
								<strong>
									Analisa EDD (Enhanced Due Dilligent) High Risk & Politically
									Exposed Person (PEP)
								</strong>
								<div style={{ minHeight: 60 }}>
									{data?.eddHighRiskAndPepAnalysis}
								</div>
							</td>
						</tr>
						<tr>
							<td>
								<center>
									<strong>VI.</strong>
								</center>
							</td>
							<td>
								<strong>CPA/PAT</strong>
								<div style={{ minHeight: 60 }}>
									{data?.cpaPatDocument?.url && (
										<img
											src={data.cpaPatDocument.url}
											style={{ display: 'block', width: '75%', height: 'auto' }}
											loading="eager"
											onError={handleImageError}
											crossOrigin="anonymous"
										/>
									)}
								</div>
								<div style={{ minHeight: 60 }}>
									Informasi CPA/PAT :
									<br />
									{data?.cpaPatInformation}
								</div>
							</td>
						</tr>
						<tr>
							<td
								colSpan={2}
								style={{ height: 100 }}
							>
								Informasi Tambahan :
								<br />
								{data?.notes}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	};

	const analysisFragment = () => {
		const { data } = getNAPAnalysisQuery?.data ?? {};

		return (
			<table>
				<thead className="cell-border">
					<tr>
						<td
							className="bg"
							colSpan={2}
						>
							<strong>D. ANALISA PEMBIAYAAN</strong>
						</td>
					</tr>
				</thead>
				<tbody>
					<tr className="cell-border">
						<td
							style={{ background: '#e7e6e6', paddingLeft: 30 }}
							colSpan={2}
						>
							<strong>1. ASPEK LEGAL DAN MANAGEMENT</strong>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Legalitas {data?.businessEntity?.value}</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Jenis Dokumen</th>
										<th
											className="bg"
											style={{ width: 150 }}
										>
											No Dokumen
										</th>
										<th
											className="bg"
											style={{ width: 150 }}
										>
											Tanggal Dokumen
										</th>
										<th
											className="bg"
											style={{ width: 300 }}
										>
											Keterangan
										</th>
									</tr>
								</thead>
								<tbody>
									{data?.legalAndManagementCategory?.customerLegalDocuments?.map(
										(record, i) => (
											<tr key={i}>
												<td>
													<center>{i + 1}</center>
												</td>
												<td>{record.documentType}</td>
												<td>{record.documentNo}</td>
												<td>
													{record.documentDate &&
														dayjs(record.documentDate).format('DD/MM/YYYY')}
												</td>
												<td>{record.notes}</td>
											</tr>
										),
									)}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Legalitas Perizinan</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th
											className="bg"
											style={{ width: 150 }}
										>
											Akta
										</th>
										<th className="bg">Jenis Dokumen</th>
										<th className="bg">Nomor</th>
										<th className="bg">Tanggal Dokumen</th>
										<th className="bg">Masa Berlaku</th>
										<th
											className="bg"
											style={{ width: 300 }}
										>
											Keterangan
										</th>
									</tr>
								</thead>
								<tbody>
									{data?.legalAndManagementCategory?.customerBusinessDocuments?.map(
										(record, i) => (
											<tr key={i}>
												<td>
													<center>{i + 1}</center>
												</td>
												<td />
												<td>{record.documentType}</td>
												<td>{record.documentNo}</td>
												<td>
													{record.documentDate &&
														dayjs(record.documentDate).format('DD/MM/YYYY')}
												</td>
												<td />
												<td>{record.notes}</td>
											</tr>
										),
									)}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>
								Legalitas Perusahaan Penjamin (apabila terdapat Pejaminan dari
								Perusahaan lain)
							</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Jenis Dokumen</th>
										<th
											className="bg"
											style={{ width: 150 }}
										>
											No Dokumen
										</th>
										<th
											className="bg"
											style={{ width: 150 }}
										>
											Tanggal Dokumen
										</th>
										<th
											className="bg"
											style={{ width: 300 }}
										>
											Keterangan
										</th>
									</tr>
								</thead>
								<tbody>
									{data?.legalAndManagementCategory?.guarantorCompanyLegalityDocuments?.map(
										(record, i) => (
											<tr key={i}>
												<td>
													<center>{i + 1}</center>
												</td>
												<td>{record.documentType}</td>
												<td>{record.documentNo}</td>
												<td>
													{record.documentDate &&
														dayjs(record.documentType).format('DD/MM/YYYY')}
												</td>
												<td>{record.notes}</td>
											</tr>
										),
									)}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>IV.</strong>
							</center>
						</td>
						<td>
							<strong>Profil Pengurus</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Nama</th>
										<th className="bg">Jenis Identitas</th>
										<th className="bg">No ID</th>
										<th className="bg">Periode Kepengurusan</th>
										<th className="bg">Pengalaman Proffesional</th>
									</tr>
								</thead>
								<tbody>
									{data?.legalAndManagementCategory?.customerManagements.length
										? data.legalAndManagementCategory.customerManagements.map(
												(record, i) => (
													<tr key={i}>
														<td>
															<center>{i + 1}</center>
														</td>
														<td>{record.name}</td>
														<td>{record.identityCardType?.value}</td>
														<td>{record.identityNumber}</td>
														<td>
															{record.startManagementPeriod &&
																record.endManagementPeriod &&
																`${dayjs(record.startManagementPeriod).format('DD/MM/YYYY')} - ${dayjs(record.endManagementPeriod).format('DD/MM/YYYY')}`}
														</td>
														<td>{record.profesionalExperience}</td>
													</tr>
												),
											)
										: Array.from({ length: 4 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>V.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi lain mengenai debitur</strong>
							<div style={{ minHeight: 60 }}>
								{data?.legalAndManagementCategory?.debiturOtherInformation}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>VI.</strong>
							</center>
						</td>
						<td>
							<strong>Struktur Organisasi dan Tenaga Kerja</strong>
							<div style={{ minHeight: 60 }}>
								<div>
									{
										data?.legalAndManagementCategory
											.organizationAndWorkforceAdditionalInfo
									}
								</div>
								{data?.legalAndManagementCategory
									?.organizationAndWorkforceDocument?.url && (
									<img
										src={
											data.legalAndManagementCategory
												.organizationAndWorkforceDocument.url
										}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
							</div>
						</td>
					</tr>
					<tr className="cell-border">
						<td
							style={{ background: '#e7e6e6', paddingLeft: 30 }}
							colSpan={2}
						>
							<strong>2. ASPEK TEKNIS</strong>
						</td>
					</tr>
					<tr>
						<td style={{ width: 80 }}>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Pola Usaha</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory?.businessPatternDocument?.url && (
									<img
										src={data.technicalCategory.businessPatternDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.technicalCategory?.businessPatternAnalysisResult}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Lokasi Kantor</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory?.officeLocation}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>Lokasi Project/Site</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory?.siteLocation}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>IV.</strong>
							</center>
						</td>
						<td>
							<strong>Proses Produksi (apabila ada)</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory?.productionProcess}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>V.</strong>
							</center>
						</td>
						<td>
							<strong>Realisasi dan Proyeksi Produksi</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory
									?.productionRealizationAndProjectionDocument?.url && (
									<img
										src={
											data.technicalCategory
												.productionRealizationAndProjectionDocument.url
										}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{
									data?.technicalCategory
										?.productionRealizationAndProjectionAdditionalInfo
								}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>VI.</strong>
							</center>
						</td>
						<td>
							<strong>Utilisasi Kebutuhan dan Analisa Produksi</strong>
							<div style={{ minHeight: 60 }}>
								{data?.technicalCategory
									?.needsAndAnalysisProductionUtilizationDocument?.url && (
									<img
										src={
											data.technicalCategory
												.needsAndAnalysisProductionUtilizationDocument.url
										}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{
									data?.technicalCategory
										?.needsAndAnalysisProductionUtilizationAdditionalInfo
								}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>VII.</strong>
							</center>
						</td>
						<td>
							<strong>Daftar dan Trade Checking Supplier</strong>
							<div>
								Trade checking dilakukan kepada supplier dengan hasil sebagai
								berikut:
							</div>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Nama Supplier</th>
										<th className="bg">Tanggal</th>
										<th className="bg">Contact Person</th>
										<th className="bg">Hasil</th>
									</tr>
								</thead>
								<tbody>
									{data?.technicalCategory?.supplierTradeVerifications?.length
										? data.technicalCategory.supplierTradeVerifications.map(
												(record, i) => (
													<tr key={i}>
														<td>
															<center>{i + 1}</center>
														</td>
														<td>{record.supplierName}</td>
														<td>
															{record.verificationDate &&
																dayjs(record.verificationDate).format(
																	'DD/MM/YYYY',
																)}
														</td>
														<td>{record.contactPerson}</td>
														<td>{record.verificationResult}</td>
													</tr>
												),
											)
										: Array.from({ length: 3 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td colSpan={2}>
							<div>Informasi Tambahan :</div>
							<div style={{ minHeight: 60 }}>
								{
									data?.technicalCategory
										?.supplierTradeVerificationAdditionalInfo
								}
							</div>
						</td>
					</tr>
					<tr className="cell-border">
						<td
							style={{ background: '#e7e6e6', paddingLeft: 30 }}
							colSpan={2}
						>
							<strong>3. ASPEK PEMASARAN</strong>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>List Kontrak Kerja</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Bouwheer</th>
										<th className="bg">Kelas Industri</th>
										<th className="bg">Nama Pekerjaan</th>
										<th className="bg">Detail Kontrak</th>
										<th className="bg">Nilai Kontrak</th>
										<th className="bg">Tanggal Mulai</th>
										<th className="bg">Tanggal Akhir</th>
									</tr>
								</thead>
								<tbody>
									{data?.marketingCategory?.employmentContracts?.length
										? data.marketingCategory.employmentContracts.map(
												(record, i) => (
													<tr key={i}>
														<td>
															<center>{i + 1}</center>
														</td>
														<td>{record.bouwheer?.name}</td>
														<td>{record.industrySector?.industryClass}</td>
														<td>{record.jobTitle}</td>
														<td>{record.contractDetail}</td>
														<td>{formatCurrency(record.contractValue ?? 0)}</td>
														<td>
															{record.startDate &&
																dayjs(record.startDate).format('DD/MM/YYYY')}
														</td>
														<td>
															{record.endDate &&
																dayjs(record.endDate).format('DD/MM/YYYY')}
														</td>
													</tr>
												),
											)
										: Array.from({ length: 3 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
							<div style={{ minHeight: 60 }}>
								Informasi Tambahan :
								<br />
								{data?.marketingCategory?.employmentContractAdditionalInfo}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Realisasi proyeksi penjualan/pekerjaan</strong>
							<div style={{ minHeight: 60 }}>
								{data?.marketingCategory
									?.salesAndWorkRealizationProjectionDocument?.url && (
									<img
										src={
											data.marketingCategory
												.salesAndWorkRealizationProjectionDocument.url
										}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{
									data?.marketingCategory
										?.salesAndWorkRealizationProjectionAdditionalInfo
								}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>Trade checking Bouwheer/Buyer</strong>{' '}
							<i>(bisnis unit)</i>
							<div>
								Trade checking dilakukan kepada buyer dengan hasil sebagai
								berikut:
							</div>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Nama Supplier</th>
										<th className="bg">Tanggal</th>
										<th className="bg">Contact Person</th>
										<th className="bg">Hasil</th>
									</tr>
								</thead>
								<tbody>
									{data?.marketingCategory?.bouwheerTradeVerifications?.length
										? data.marketingCategory.bouwheerTradeVerifications.map(
												(record, i) => (
													<tr key={i}>
														<td>
															<center>{i + 1}</center>
														</td>
														<td>{record.supplierName}</td>
														<td>
															{record.verificationDate &&
																dayjs(record.verificationDate).format(
																	'DD/MM/YYYY',
																)}
														</td>
														<td>{record.contactPerson}</td>
														<td>{record.verificationResult}</td>
													</tr>
												),
											)
										: Array.from({ length: 3 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td colSpan={2}>
							<div>Informasi Tambahan :</div>
							<div style={{ minHeight: 60 }}>
								{
									data?.marketingCategory
										?.bouwheerTradeVerificationAdditionalInfo
								}
							</div>
						</td>
					</tr>
					<tr className="cell-border">
						<td
							style={{ background: '#e7e6e6', paddingLeft: 30 }}
							colSpan={2}
						>
							<strong>4. ASPEK KEUANGAN</strong>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Informasi KAP</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Tahun</th>
										<th className="bg">KAP</th>
										<th className="bg">Opini</th>
										<th className="bg">Keterangan</th>
									</tr>
								</thead>
								<tbody>
									{data?.financialCategory?.kapInformations?.length
										? data.financialCategory.kapInformations.map(
												(record, i) => (
													<tr key={i}>
														<td>
															<center>{i + 1}</center>
														</td>
														<td>{record.year}</td>
														<td>{record.kap}</td>
														<td>{record.opinion}</td>
														<td>{record.description}</td>
													</tr>
												),
											)
										: Array.from({ length: 3 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Neraca</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.balanceSheetDocument?.url && (
									<img
										src={data.financialCategory.balanceSheetDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.balanceSheetAnalysisResult}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>III.</strong>
							</center>
						</td>
						<td>
							<strong>Laba Rugi</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.profitAndLossDocument?.url && (
									<img
										src={data.financialCategory.profitAndLossDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.profitAndLossAnalysisResult}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>IV.</strong>
							</center>
						</td>
						<td>
							<strong>Rasio Keuangan</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.financialRatioDocument?.url && (
									<img
										src={data.financialCategory.financialRatioDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.financialRatiosAnalysisResult}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>V.</strong>
							</center>
						</td>
						<td>
							<strong>Mutasi Rekening</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.bankStatementAnalysisDocument
									?.url && (
									<img
										src={
											data.financialCategory.bankStatementAnalysisDocument.url
										}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.bankStatementAnalysisAdditionalInfo}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>VI.</strong>
							</center>
						</td>
						<td>
							<strong>
								Analisa Pembiayaan (Skema Pembiayaan dan Repayment Capacity)
							</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.repaymentCapacityDocument?.url && (
									<img
										src={data.financialCategory.repaymentCapacityDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.repaymentCapacityAdditionalInfo}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>VII.</strong>
							</center>
						</td>
						<td>
							<strong>
								Proyeksi Cashflow (termasuk asumsi yang digunakan untuk proyeksi
								keuangan)
							</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.cashFlowProjectionDocument?.url && (
									<img
										src={data.financialCategory.cashFlowProjectionDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.cashFlowProjectionAdditionalInfo}
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>VIII.</strong>
							</center>
						</td>
						<td>
							<strong>Evaluasi Guarantor apabila ada CG/PG</strong>
							<div style={{ minHeight: 60 }}>
								{data?.financialCategory?.guarantorLetterDocument?.url && (
									<img
										src={data.financialCategory.guarantorLetterDocument.url}
										style={{ display: 'block', width: '75%', height: 'auto' }}
										loading="eager"
										onError={handleImageError}
										crossOrigin="anonymous"
									/>
								)}
								<br />
								{data?.financialCategory?.guarantorEvaluationAdditionalInfo}
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const riskMitigation = () => {
		const { data } = getNAPRiskMitigationQuery.data ?? {};

		return (
			<table>
				<thead className="cell-border">
					<tr>
						<td className="bg">
							<strong>E. RISIKO DAN MITIGASI</strong>
						</td>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											className="bg"
											style={{ width: 40 }}
										>
											No
										</th>
										<th className="bg">Risiko</th>
										<th className="bg">Mitigasi</th>
									</tr>
								</thead>
								<tbody>
									{data?.riskAndMitigations.length
										? data.riskAndMitigations.map((record, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td>{record.risk}</td>
													<td>{record.mitigation}</td>
												</tr>
											))
										: Array.from({ length: 3 }).map((_, i) => (
												<tr key={i}>
													<td>
														<center>{i + 1}</center>
													</td>
													<td></td>
													<td></td>
												</tr>
											))}
								</tbody>
							</table>
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const recommendation = () => {
		const { data } = getNAPRecommendationQuery?.data ?? {};

		return (
			<table>
				<thead className="cell-border">
					<tr>
						<td
							colSpan={2}
							className="bg"
						>
							<strong>F. REKOMENDASI</strong>
						</td>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td colSpan={2}>
							<div>
								Berdasarkan evaluasi di atas maka kami mengusulkan untuk dapat
								disetujui fasilitas pembiayaan atas nama{' '}
								<strong>{mainInformation?.customerName}</strong> dengan
								ketentuan dan syarat sebagai berikut :
							</div>
						</td>
					</tr>
					<tr>
						<td style={{ width: 60 }}>
							<center>
								<strong>I.</strong>
							</center>
						</td>
						<td>
							<strong>Ketentuan</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<th
											style={{ width: 230 }}
											className="bg"
										>
											Perihal
										</th>
										<th className="bg">Usulan</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>Limit BPP</td>
										<td>Rp.{formatCurrency(data?.bppLimit ?? 0)}</td>
									</tr>
									<tr>
										<td>Jangka Waktu Plafon</td>
										<td>{data?.plafonPeriod}</td>
									</tr>
									<tr>
										<td>Jenis Pembiayaan</td>
										<td>
											{data?.financingTypeOptions?.map((record) => (
												<Space
													key={record.id}
													style={{ display: 'flex' }}
												>
													{data?.financingTypes.some((s) => s.id === record.id)
														? '[x]'
														: '[ ]'}
													<strong>{record.value}</strong>
												</Space>
											))}
										</td>
									</tr>
									<tr>
										<td>Sifat Limit Pembiayaan</td>
										<td>
											{data?.financingLimitTypeOptions?.map((record) => (
												<Space
													key={record.id}
													style={{ display: 'flex' }}
												>
													{data?.financingLimitType?.id === record.id
														? '[x]'
														: '[ ]'}
													<strong>{record.value}</strong>
												</Space>
											))}
										</td>
									</tr>
									<tr>
										<td>Objek Pembiayaan</td>
										<td>
											{data?.financingObjectOptions?.map((record) => (
												<Space
													key={record.id}
													style={{ display: 'flex' }}
												>
													{data?.financingObjects.some(
														(s) => s.id === record.id,
													)
														? '[x]'
														: '[ ]'}
													<strong>{record.value}</strong>
												</Space>
											))}
										</td>
									</tr>
									<tr>
										<td>Down Payment</td>
										<td>{data?.downPayment ? `${data.downPayment}%` : '-'}</td>
									</tr>
									<tr>
										<td>Suku Bunga Efektif</td>
										<td>
											{data?.effectiveInterestRate
												? `${data.effectiveInterestRate}%`
												: '-'}
										</td>
									</tr>
									<tr>
										<td>Tenor</td>
										<td>{data?.tenor ? `${data.tenor} Bulan` : '-'}</td>
									</tr>
									<tr>
										<td>Biaya Administrasi</td>
										<td>{data?.administrationFee ?? '-'}</td>
									</tr>
									<tr>
										<td>Provisi</td>
										<td>
											{data?.provisionFee ? `${data.provisionFee}%` : '-'}
										</td>
									</tr>
									<tr>
										<td>Servicing Fee</td>
										<td>{data?.servicingFee ?? '-'}</td>
									</tr>
									<tr>
										<td>Fidusia</td>
										<td>{data?.fiduciaryFee ?? '-'}</td>
									</tr>
									<tr>
										<td>Asuransi</td>
										<td>
											{data?.insuranceOptions?.map((record) => (
												<Space
													key={record.id}
													style={{ display: 'flex' }}
												>
													{data?.insurance?.id === record.id ? '[x]' : '[ ]'}
													<strong>{record.value}</strong>
												</Space>
											))}
										</td>
									</tr>
									<tr>
										<td>Way of Payment (WOP)</td>
										<td>{data?.paymentMethod?.value ?? '-'}</td>
									</tr>
									<tr>
										<td>Pengajuan Kondisi Khusus</td>
										<td>{data?.specialCondition ?? '-'}</td>
									</tr>
									<tr>
										<td>Evidence Pengajuan Kondisi Khusus</td>
										<td>
											{data?.evidenceFile?.url && (
												<img
													src={data.evidenceFile.url}
													style={{
														display: 'block',
														width: '75%',
														height: 'auto',
													}}
													loading="eager"
													onError={handleImageError}
													crossOrigin="anonymous"
												/>
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</td>
					</tr>
					<tr>
						<td>
							<center>
								<strong>II.</strong>
							</center>
						</td>
						<td>
							<strong>Ketentuan & Syarat Kredit</strong>
							<table className="cell-small cell-border">
								<thead>
									<tr>
										<td className="bg">
											<strong>Ketentuan & Syarat</strong>
										</td>
										<td className="bg">
											<strong>Kategori</strong>
										</td>
										<td className="bg">
											<strong>Priority</strong>
										</td>
									</tr>
								</thead>
								<tbody>
									{data?.creditTermsAndConditions?.length ? (
										data.creditTermsAndConditions.map((record, i) => (
											<tr key={i}>
												<td>{record.termAndCondition}</td>
												<td>{record.category?.value}</td>
												<td>{record.priority?.value}</td>
											</tr>
										))
									) : (
										<Fragment>
											<tr>
												<td>&nbsp;</td>
												<td></td>
												<td></td>
											</tr>
											<tr>
												<td>&nbsp;</td>
												<td></td>
												<td></td>
											</tr>
										</Fragment>
									)}
								</tbody>
							</table>
						</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const renderSignature = (name: string) => {
		return (
			<center style={{ paddingTop: 200, fontWeight: 'bold' }}>
				(______________)
				<br />
				{name}
			</center>
		);
	};

	const categoryA = () => {
		return (
			<Fragment>
				<div>
					<strong>*lembar rekomendasi untuk kewenangan kategori A</strong>
					<center>
						<strong>
							NAP NON JF WHOLESALE No. {napNo} Tgl.{' '}
							{dayjs().utc(true).format('DD-MM-YYYY')}
							<br />
							{mainInformation?.customerName}
						</strong>
					</center>
				</div>

				<table>
					<tbody className="cell-border">
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								PENGUSUL
							</th>
						</tr>
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								CORPORATE FLEET
							</th>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>
								{renderSignature('Fleet Relationship Manager')}
							</td>
							<td style={{ width: '50%' }}>
								{renderSignature('Fleet Business Departemen Head')}
							</td>
						</tr>
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								CORPORATE CREDIT MANAGEMENT
							</th>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>
								{renderSignature('Corporate Credit Analyst')}
							</td>
							<td style={{ width: '50%' }}>
								{renderSignature('Corporate Credit Departemen Head')}
							</td>
						</tr>
					</tbody>
				</table>

				<table>
					<tbody className="cell-border">
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								RECOMMENDING COMMITEE
							</th>
						</tr>
						<tr>
							<th className="bg">CORPORATE FLEET</th>
							<th className="bg">CORPORATE CREDIT MANAGEMENT</th>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>
								{renderSignature('Division Head Corporate Fleet1/2')}
							</td>
							<td style={{ width: '50%' }}>
								{renderSignature(
									'Division Head Corporate Credit Management/Executive Credit Officer',
								)}
							</td>
						</tr>
					</tbody>
				</table>

				<p style={{ marginBottom: 200 }}>
					Risalah keputusan Komite Kategori A_ disajikan dalam lembar terpisah
					namun merupakan kesatuan dengan NAP
					<br />
					No. {napNo} Tgl. {dayjs().utc(true).format('DD-MM-YYYY')}
				</p>
			</Fragment>
		);
	};

	const categoryB = () => {
		return (
			<Fragment>
				<div>
					<strong>*lembar rekomendasi untuk kewenangan kategori B</strong>
					<center>
						<strong>
							NAP NON JF WHOLESALE No. {napNo} Tgl.{' '}
							{dayjs().utc(true).format('DD-MM-YYYY')}
							<br />
							{mainInformation?.customerName}
						</strong>
					</center>
				</div>

				<table>
					<tbody className="cell-border">
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								PENGUSUL
							</th>
						</tr>
						<tr>
							<th className="bg">CORPORATE FLEET</th>
							<th className="bg">CORPORATE CREDIT MANAGEMENT</th>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>
								{renderSignature('Fleet Relationship Manager')}
							</td>
							<td style={{ width: '50%' }}>
								{renderSignature('Corporate Credit Analyst')}
							</td>
						</tr>
					</tbody>
				</table>

				<table>
					<tbody className="cell-border">
						<tr>
							<th
								colSpan={2}
								className="bg"
							>
								RECOMMENDING COMMITEE
							</th>
						</tr>
						<tr>
							<th className="bg">CORPORATE FLEET</th>
							<th className="bg">CORPORATE CREDIT MANAGEMENT</th>
						</tr>
						<tr>
							<td style={{ width: '50%' }}>
								{renderSignature('Fleet Business Departemen Head')}
							</td>
							<td style={{ width: '50%' }}>
								{renderSignature('Corporate Credit Departemen Head')}
							</td>
						</tr>
					</tbody>
				</table>

				<p style={{ marginBottom: 200 }}>
					Risalah keputusan Komite Kategori B_ disajikan dalam lembar terpisah
					namun merupakan kesatuan dengan NAP
					<br />
					No. {napNo} Tgl. {dayjs().utc(true).format('DD-MM-YYYY')}
				</p>
			</Fragment>
		);
	};

	const surveyPhoto = () => {
		return (
			<div>
				<strong>Foto Survey</strong>
				<table className="cell-border">
					<tbody>
						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								1. Foto dengan Debitur/Pengurus
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Harap mendokumentasikan sebagai berikut :</p>
								<ul>
									<li>Wajah PIC Debitur dan PIC MTF terlihat dengan jelas</li>
									<li>
										Foto menunjukkan keberadaan PIC MTF di kantor debitur dan
										tidak dalam bentuk <i>selfie</i>
									</li>
								</ul>
							</td>
						</tr>

						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								2. Foto Kantor
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Harap mendokumentasikan sebagai berikut :</p>
								<ul>
									<li>
										Foto kantor dapat menunjukkan aktivitas kantor (aktivitas
										karyawan)
									</li>
									<li>
										Foto kantor harus dapat menunjukkan fasilitas/alat pendukung
										usaha
									</li>
								</ul>
							</td>
						</tr>

						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								3. Foto Kantor Operasional/Lokasi Usaha*
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Foto kantor operasional harus dapat menunjukkan :</p>
								<ol>
									<li>Kegiatan operasional bisnis</li>
									<li>
										Fasilitas penunjang/pendukung usaha (cont. alat/mesin)
									</li>
								</ol>
							</td>
						</tr>

						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								4. Foto Aktivitas Usaha
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Foto aktivitas usaha harus dapat menunjukkan :</p>
								<ol>
									<li>
										Menunjukkan proses produksi/adding value dari customer
									</li>
									<li>
										Usahakan foto diambil pada saat aktivitas sedang berlangsung
										(jika diperbolehkan)
									</li>
								</ol>
							</td>
						</tr>

						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								5. Inventory/kendaraan*
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Foto inventory harus dapat menunjukkan :</p>
								<ol>
									<li>
										Menunjukkan persediaan yang dimiliki debitur
										(Gudang/stockpile/port)
									</li>
									<li>
										Menunjukkan persediaan yang dimiliki debitur
										(Gudang/stockpile/port)
									</li>
								</ol>
							</td>
						</tr>

						<tr>
							<td
								colSpan={2}
								className="bg"
							>
								6. Foto Agunan*
							</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td>&nbsp;</td>
							<td>&nbsp;</td>
						</tr>
						<tr>
							<td colSpan={2}>
								<p>Foto agunan harus dapat menunjukkan :</p>
								<ol>
									<li>
										Brand, model dan kondisi unit dengan jelas dan terkini (jika
										unit banyak maka foto inventory/pool)
									</li>
									<li>Batas-batas kepemilikan yang jelas (property)</li>
								</ol>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	};

	return (
		<div
			id="download-nap"
			className="download-nap"
			style={{ width: '100%', maxWidth: '1600px' }}
		>
			<div style={{ width: '100%', margin: '0 auto' }}>
				{wholesaleFragment()}
				{applicationBGFragment()}
				{companyGroupFragment()}
				{fullfillmentFragment()}
				{analysisFragment()}
				{riskMitigation()}
				{recommendation()}
				{categoryA()}
				{categoryB()}
				{surveyPhoto()}
			</div>
		</div>
	);
};

export default NAPDownloadPage;

export const useNAPNJFModal = () => {
	return useAsyncModal<{ prospectId: string }, void>();
};

export const NAPNJFModal: FC<ReturnType<typeof useNAPNJFModal>> = ({
	modal,
	data,
}) => {
	return (
		<Modal
			{...modal}
			okButtonProps={{
				hidden: true,
			}}
			cancelButtonProps={{
				hidden: true,
			}}
			width={1200}
		>
			{data?.prospectId && (
				<NAPDownloadPage
					prospectId={data.prospectId}
					preview
				/>
			)}
		</Modal>
	);
};
